import { Box, Stack } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import { useMutation } from '@tanstack/react-query';
import { useRouter, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import * as z from 'zod';

import { tracking } from '#features/analytics';
import { passwordMethod, ReCaptchaCaption, useForm, useReCaptcha } from '#features/form';
import { Button, Link, Loading, Notice, PageHeading } from '#features/ui';
import { PasswordInputField } from '#features/ui/components/inputs/PasswordInput';
import { TextInputField } from '#features/ui/components/inputs/TextInput';
import { useAuth } from '../../hooks/useAuth';
import { doLogin } from '../../model/login';
import { AuthLegalNotice } from '../AuthLegalNotice';
import { AuthMethods } from '../AuthMethods';
import { AuthPageLayout } from '../AuthPageLayout';

const validationSchema = z.object({
  email: z
    .email({ error: (email) => (!email.input ? 'required' : 'email.wrongFormat') })
    .min(1, 'required'),
  password: passwordMethod(),
});

export const SignInForm = () => {
  const { setReCaptchaToken } = useReCaptcha();
  const { provider, redirect } = useSearch({ from: '/{-$locale}/_layout/sign-in' });
  const { t } = useLingui();
  const { handleSubmit, nativeBackProps, control, formState } = useForm({
    validationSchema,
  });
  const { login } = useAuth();
  const loginMutation = useMutation({
    mutationFn: doLogin,
  });
  const router = useRouter();
  /*
    Отказ сервера живёт на экране, а не во всплывающей плашке.

    «Invalid email or password» сервер намеренно не привязывает ни к почте, ни к паролю —
    иначе по ответу можно перебором узнать, какие адреса у нас зарегистрированы. Поэтому
    подсветить поле нечем, и текст стоит над формой. Раньше он жил тостом на пять секунд: тот
    всплывал в правом верхнем углу, на телефоне ложился поверх шапки и исчезал — человек
    возвращался к форме, в которой ничто не намекало, что пошло не так.
  */
  const [serverError, setServerError] = useState<string | null>(null);

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    tracking.event({ action: 'submit_sign_in', category: 'sign_in' });
    if (!(await setReCaptchaToken('signIn'))) return;
    const data = await loginMutation.mutateAsync({
      email: values.email,
      password: values.password,
    });

    if (data?.__typename === 'Token') {
      login();
      router.invalidate();
    }

    if (data?.__typename === 'GlobalError') {
      setServerError(data.errorMessage ?? t`common:error.generic`);
    }
  });

  return (
    <AuthPageLayout languageDisabled={formState.isSubmitting || Boolean(provider)}>
      <PageHeading
        align="center"
        description={
          <>
            {t`signIn.noAccount`}{' '}
            <Link to="/{-$locale}/sign-up" search={{ redirect }} color="fg.link">
              {t`signIn.createAccount`}
            </Link>
          </>
        }
      >
        {t`signIn.welcome`}
      </PageHeading>
      <Box position="relative">
        {provider && (
          <Box
            position="absolute"
            left="50%"
            top="50%"
            display="block"
            transform="translate(-50%, -50%)"
          >
            <Loading variant="inline" />
          </Box>
        )}
        <Stack gap={6} visibility={provider ? 'hidden' : 'visible'}>
          <AuthMethods
            mode="sign-in"
            emailError={Boolean(serverError) || Object.keys(formState.errors).length > 0}
            emailBusy={formState.isSubmitting}
          >
            <Stack gap={4}>
              {/* Отказ приходит только от входа по почте, поэтому стоит над её формой, под соцсетями. */}
              {serverError && (
                <Notice tone="danger" role="alert" aria-live="polite">
                  {serverError}
                </Notice>
              )}
              <Stack as="form" {...nativeBackProps} gap={4} onSubmit={onSubmit}>
                {/*
              Подсказки браузеру, какое это поле: без них менеджер паролей не понимал, что
              перед ним форма входа, подставлял что попало и не предлагал сохранить пару.
            */}
                <TextInputField
                  control={control}
                  name="email"
                  label="Email"
                  autoComplete="username"
                />
                <Stack gap={2}>
                  <PasswordInputField
                    control={control}
                    name="password"
                    label={t`password`}
                    autoComplete="current-password"
                  />
                  {/*
                «Забыли пароль?» у самого поля пароля: его ищут ровно в тот момент, когда
                не могут вспомнить пароль. Раньше он стоял под кнопкой белым текстом без
                цвета ссылки и читался как подпись.
              */}
                  <Link
                    to="/{-$locale}/forgot-password"
                    color="fg.link"
                    textStyle="caption"
                    alignSelf="flex-end"
                  >
                    {t`signIn.forgotPassword`}
                  </Link>
                </Stack>
                <Button type="submit" size="lg" loading={formState.isSubmitting}>
                  {t`signIn.signIn`}
                </Button>
              </Stack>
            </Stack>
          </AuthMethods>
          {/* Мелкий юридический текст одним блоком внизу, а не между кнопкой и ссылками. */}
          <Stack gap={2} textAlign="center" color="fg.muted">
            <ReCaptchaCaption />
            <AuthLegalNotice />
          </Stack>
        </Stack>
      </Box>
    </AuthPageLayout>
  );
};
